import { useEffect, useRef } from "react";

type ScrollToSelectedValueProps = {
  /** Array of string values in the list */
  values: string[];
  /** The value to scroll into view */
  selectedValue: string | undefined;
  /** Boolean indicating if scrolling is disabled */
  disabled: boolean;
};

export function useScrollToSelectedValue({
  values,
  selectedValue,
  disabled,
}: ScrollToSelectedValueProps) {
  const padding = useRef<number>(0);
  const scrollListRef = useRef<HTMLUListElement>(null);

  /**
   * Effect to calculate the padding based on the position of the first child element.
   */
  useEffect(() => {
    if (!scrollListRef.current || !scrollListRef.current.childNodes.length)
      return;

    const listElement = scrollListRef.current;
    const firstCard = listElement.childNodes[0] as HTMLLIElement;

    // Get the current scroll position and bounding rectangles of the list and first card.
    const { scrollLeft } = listElement;
    const { left: containerLeft } = listElement.getBoundingClientRect();
    const { left: cardLeft } = firstCard.getBoundingClientRect();

    // Calculate the padding based on the difference in positions.
    padding.current = cardLeft - containerLeft + scrollLeft;
  }, [scrollListRef]);

  /**
   * Effect to scroll the list to center the selected value.
   */
  useEffect(() => {
    if (!selectedValue || disabled || !scrollListRef.current) return;

    // Find the index of the selected value and
    // return early if the selected index is invalid.
    const selectedIndex = values.findIndex((value) => value === selectedValue);
    if (
      selectedIndex < 0 ||
      selectedIndex >= scrollListRef.current.childNodes.length
    )
      return;

    const listElement = scrollListRef.current;
    const { clientWidth } = listElement;
    const selectedChild = listElement.childNodes[
      selectedIndex
    ] as HTMLLIElement;

    // Calculate the center position for scrolling.
    const cardWidth = selectedChild.clientWidth;
    const centerPosition =
      padding.current +
      selectedChild.offsetLeft +
      cardWidth / 2 -
      clientWidth / 2;

    // Smoothly scroll the list to the calculated center position.
    listElement.scrollTo?.({ left: centerPosition, behavior: "smooth" });
  }, [values, selectedValue, disabled]);

  return scrollListRef;
}
